import cn from "classnames";
import { ReactNode, TransitionEvent, useEffect, useState } from "react";

interface ConditionalFieldGroupProps {
  /** Whether the grouped content is shown */
  visible: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Generic expand/collapse wrapper for content that should only appear when a
 * controlling field is set (e.g. call sign formatting options once a call sign
 * is entered). Animates open/closed with a smooth height + fade transition
 * using the CSS grid 0fr -> 1fr technique, so it works for content of any
 * height. `visibility` flips at the end of the transition so collapsed
 * content can't be tabbed into.
 *
 * Overflow is only clipped while collapsed/animating; once fully open it is
 * released so absolutely-positioned popovers inside (dropdown menus) aren't
 * cut off at the group's edge.
 */
export default function ConditionalFieldGroup({
  visible,
  children,
  className,
}: ConditionalFieldGroupProps) {
  // True once the expand transition has finished — only then is it safe to
  // stop clipping overflow.
  const [fullyOpen, setFullyOpen] = useState(visible);

  useEffect(() => {
    if (!visible) setFullyOpen(false);
  }, [visible]);

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && visible) setFullyOpen(true);
  };

  return (
    <div
      className={cn(
        "grid transition-all duration-300 ease-in-out",
        visible
          ? "grid-rows-[1fr] opacity-100 visible"
          : "grid-rows-[0fr] opacity-0 invisible"
      )}
      aria-hidden={!visible}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={cn(
          visible && fullyOpen ? "overflow-visible" : "overflow-hidden"
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-y-2 pt-2 pl-4 border-l-2 border-base-300 ml-1",
            className
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
